import { useTranslation } from 'react-i18next';
import { Check, AlertTriangle, PauseCircle } from 'lucide-react';

import { cn } from '@/lib/utils';
import type { MatterPhase } from '../types';

// The forward-only legal path. PROVISIONAL_OPEN is rendered as an emergency
// branch on the INTAKE slot; SUSPENDED mutes the whole pipeline.
const MAIN_PATH: MatterPhase[] = [
  'INTAKE',
  'PLANNING',
  'EXECUTION',
  'REVIEW',
  'CLOSED',
];

interface MatterPhaseStepperProps {
  phase: MatterPhase | undefined;
}

export function MatterPhaseStepper({ phase }: MatterPhaseStepperProps) {
  const { t } = useTranslation();
  if (!phase) return null;

  const isSuspended = phase === 'SUSPENDED';
  const isEmergency = phase === 'PROVISIONAL_OPEN';
  // PROVISIONAL_OPEN sits beside INTAKE (the matter is not yet PLANNING).
  const currentIndex = isEmergency ? 0 : MAIN_PATH.indexOf(phase);

  return (
    <div className="flex items-center gap-0 overflow-x-auto py-1" aria-label="lifecycle">
      {MAIN_PATH.map((step, index) => {
        const isDone = !isSuspended && index < currentIndex;
        const isCurrent = !isSuspended && index === currentIndex;
        const showEmergencyNode = isEmergency && index === 0;

        return (
          <div key={step} className="flex items-center min-w-0">
            {index > 0 && (
              <div
                className={cn(
                  'h-0.5 w-6 sm:w-10 lg:w-16 shrink-0 rounded',
                  isDone || isCurrent ? 'bg-indigo-500' : 'bg-slate-200',
                )}
              />
            )}
            <div className="flex items-center gap-2 px-1.5">
              <div
                className={cn(
                  'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 text-xs font-semibold transition-colors',
                  showEmergencyNode
                    ? 'border-orange-500 bg-orange-500 text-white'
                    : isDone
                      ? 'border-indigo-600 bg-indigo-600 text-white'
                      : isCurrent
                        ? 'border-indigo-600 bg-white text-indigo-600 ring-4 ring-indigo-100'
                        : 'border-slate-300 bg-white text-slate-400',
                )}
              >
                {showEmergencyNode ? (
                  <AlertTriangle className="h-3.5 w-3.5" />
                ) : isDone ? (
                  <Check className="h-4 w-4" />
                ) : (
                  index + 1
                )}
              </div>
              <div className="hidden sm:block min-w-0">
                <p
                  className={cn(
                    'text-xs font-medium whitespace-nowrap',
                    showEmergencyNode
                      ? 'text-orange-600'
                      : isCurrent
                        ? 'text-indigo-700'
                        : isDone
                          ? 'text-slate-700'
                          : 'text-slate-400',
                  )}
                >
                  {showEmergencyNode
                    ? t('phase.PROVISIONAL_OPEN')
                    : t(`phase.${step}`)}
                </p>
              </div>
            </div>
          </div>
        );
      })}

      {isSuspended && (
        <span className="ml-3 inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600 whitespace-nowrap">
          <PauseCircle className="h-3.5 w-3.5" />
          {t('phase.SUSPENDED')}
        </span>
      )}
    </div>
  );
}
